/**
 * Centralized formatting utilities for consistent data presentation
 */
import { CPF_LENGTH, isValidCpf, normalizeCpf } from "./validators/cpf";

/**
 * Formats currency values consistently across the application
 * @param value - The numeric value to format
 * @returns Formatted currency string in Brazilian Real (BRL)
 */
export const formatCurrency = (value: number): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return 'R$ 0,00';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Formats numbers with thousands separator
 * @param value - The numeric value to format
 * @returns Formatted number string
 */
export const formatNumber = (value: number): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '0';
  }

  return new Intl.NumberFormat('pt-BR').format(value);
};

/**
 * Formats a percentage value with two decimal places in pt-BR, suffixed with `%`.
 * Não limita o valor a 100% — percentuais acima de 100 são exibidos como vêm da API.
 * @param value - The numeric percentage value (e.g. 60 → "60,00%")
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '0,00%';
  }

  return `${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}%`;
};

/** Formats a Brazilian company document while preserving unexpected legacy values. */
export const formatCnpj = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 14) return value;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

/**
 * Formats date consistently across the application
 * @param date - The date to format
 * @returns Formatted date string in Brazilian format
 */
export const formatDate = (date: Date = new Date()): string => {
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Formats date and time consistently across the application
 * @param date - The date to format
 * @returns Formatted date and time string in Brazilian format
 */
export const formatDateTime = (date: Date = new Date()): string => {
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Calculates installment values with proper rounding to ensure sum equals total
 * The difference from rounding is added to the last installment
 * @param total - The total amount to be divided
 * @param numberOfInstallments - Number of installments
 * @returns Object with installment value and adjusted last installment value
 */
export const calculateInstallments = (
  total: number,
  numberOfInstallments: number
): {
  installmentValue: number;
  lastInstallmentValue: number;
  allInstallmentsEqual: boolean;
} => {
  const safeTotal = typeof total === "number" && !isNaN(total) ? total : 0;
  const numInstallments =
    typeof numberOfInstallments === "number" &&
      !isNaN(numberOfInstallments) &&
      numberOfInstallments > 0
      ? numberOfInstallments
      : 1;

  if (safeTotal <= 0 || numInstallments <= 0) {
    return {
      installmentValue: 0,
      lastInstallmentValue: 0,
      allInstallmentsEqual: true,
    };
  }

  // Calculate base installment value
  const baseInstallment = safeTotal / numInstallments;

  // Round to 2 decimal places
  const roundedInstallment = Math.round(baseInstallment * 100) / 100;

  // Calculate the sum of (n-1) installments
  const sumOfRegularInstallments = roundedInstallment * (numInstallments - 1);

  // Last installment is the difference to ensure total is exact
  const lastInstallment = Math.round((safeTotal - sumOfRegularInstallments) * 100) / 100;

  // Check if all installments are equal (difference is negligible)
  const allEqual = Math.abs(lastInstallment - roundedInstallment) < 0.01;

  return {
    installmentValue: roundedInstallment,
    lastInstallmentValue: lastInstallment,
    allInstallmentsEqual: allEqual,
  };
};

export interface InstallmentInfo {
  calcQtd: number;
  label: string;
  subLabel: string;
  isAVista: boolean;
}

/**
 * Resolves installment display label and calculation quantity based on business rules:
 * - 0 or 99: "À vista" (1 installment)
 * - 1: "1 parcela" (1 installment)
 * - N > 1: "N parcelas" (N installments)
 * - undefined/null: fallback to default number of installments
 */
export const resolveInstallmentInfo = (
  specificQtd: number | undefined | null,
  generalQtd: number | undefined | null,
  defaultFallback: number
): InstallmentInfo => {
  const targetQtd = specificQtd ?? generalQtd;

  if (targetQtd === 0 || targetQtd === 99) {
    return {
      calcQtd: 1,
      label: "À vista",
      subLabel: "à vista",
      isAVista: true,
    };
  }

  if (targetQtd === 1) {
    return {
      calcQtd: 1,
      label: "1 parcela",
      subLabel: "em 1 parcela",
      isAVista: false,
    };
  }

  if (typeof targetQtd === "number" && targetQtd > 1) {
    return {
      calcQtd: targetQtd,
      label: `${targetQtd} parcelas`,
      subLabel: `em ${targetQtd} parcelas`,
      isAVista: false,
    };
  }

  return {
    calcQtd: defaultFallback,
    label: `${defaultFallback} parcelas`,
    subLabel: `em ${defaultFallback} parcelas`,
    isAVista: false,
  };
};

/**
 * Formats a Brazilian postcode (CEP) as 00000-000
 * @param value - CEP with or without mask
 * @returns Masked CEP; values without 8 digits are returned as typed
 */
export const formatCep = (value?: string | null): string => {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits.length === 8 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : (value ?? '');
};

/**
 * Máscara de digitação do CPF: 000.000.000-00, aplicada conforme os dígitos são digitados (no máximo 11).
 * @param value - CPF com ou sem máscara, completo ou parcial
 */
export const formatCpf = (value?: string | null): string => {
  const digits = normalizeCpf(value).slice(0, CPF_LENGTH);
  return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9)]
    .filter(Boolean)
    .join('.')
    .concat(digits.length > 9 ? `-${digits.slice(9)}` : '');
};

/**
 * CPF mascarado para exibição: ***.456.789-** (do 4º ao 9º dígito), igual ao cpfMasked da API.
 * CPF inválido ou vazio → ***.***.***-**.
 */
export const maskCpf = (value?: string | null): string => {
  if (!isValidCpf(value)) return '***.***.***-**';
  const digits = normalizeCpf(value);
  return `***.${digits.slice(3, 6)}.${digits.slice(6, 9)}-**`;
};

/**
 * Máscara de telefone com DDD: (00) 0000-0000 ou (00) 00000-0000, aplicada conforme a digitação (no máximo 11 dígitos).
 * @param value - Telefone com ou sem máscara
 */
export const formatPhone = (value?: string | null): string => {
  const digits = (value ?? '').replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  const local = digits.slice(2);
  const split = digits.length === 11 ? 5 : 4;
  return local.length > split
    ? `(${digits.slice(0, 2)}) ${local.slice(0, split)}-${local.slice(split)}`
    : `(${digits.slice(0, 2)}) ${local}`;
};
